import React, { useCallback, useEffect, useMemo, useRef } from "react";
import type { FieldErrors, FieldPath, FieldPathValue, FieldValues, SetValueConfig } from "react-hook-form";
import type { CheckboxProps } from "@shared/ui/checkbox";
import type { CalendarDateKey } from "@shared/ui/input";
import { useFormSubmissionMemory, type FormSubmissionMemoryOptions } from "@app/form-submission-memory";
import { reportError } from "@app/services/error-reporter";
import {
    Form as FormPrimitive,
    type FormFieldBinding,
    type FormFieldControlledPropsWithLabel,
    type FormFieldInputProps,
    type FormFieldNativeStringChangeEvent,
    type FormHandle,
    type FormProps,
    type FormRenderProps,
    type FormSchemaConfig,
} from "@src/components/form";
import { RenderWhen } from "@src/shared/components/render-when/RenderWhen";
import {
    showDefaultBusinessFormValidationWarning,
    toBusinessFormSubmitErrorContext,
    type BusinessFormOnError,
    type BusinessFormSubmitErrorContext,
    type BusinessFormValidationCaptionOptions,
} from "./businessFormValidationError";
import { buildSetValueValidationDepsMap, type FormConfigForBusinessForm } from "./form-config";
import { resolveBusinessFormInitialValues } from "./resolveBusinessFormInitialValues";

/** Метаданные поля для текущего состояния формы. */
export type BusinessFormFieldMeta = {
    label?: string;
    description?: string;
    required?: boolean;
    disabled?: boolean;
    visible?: boolean;
};

/** Опции `BusinessForm.setValue`. */
export type BusinessFormSetValueOptions = SetValueConfig & {
    allowDisabledWrite?: boolean;
    reason?: string;
};

/** Расширенный binding API поля. */
export type BusinessFormFieldBinding<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>,
> = FormFieldBinding<TFieldValues, TName> & {
    name: TName;
    getUiProps: () => Pick<BusinessFormFieldMeta, "label" | "description" | "required" | "disabled">;
    getMeta: () => BusinessFormFieldMeta;
    getSearchSelectProps: <TEntity extends object>(
        mapEntityToValue: (entity: TEntity | null) => FieldPathValue<TFieldValues, TName> | null,
    ) => FormFieldControlledPropsWithLabel<TFieldValues, TName> & {
        onChange: (entity: TEntity | null) => void;
    };
    getDateInputProps: () => FormFieldControlledPropsWithLabel<TFieldValues, TName> & {
        value?: CalendarDateKey | null;
        onChange: (next: FieldPathValue<TFieldValues, TName>) => void;
    };
    getNumberInputProps: (
        options?: BusinessFormNullableNumberInputOptions | BusinessFormStrictNumberInputOptions,
    ) => BusinessFormNumberInputProps<TFieldValues, TName>;
    /**
     * Пропсы для `Checkbox`-контрола. \
     * Доступен только для `boolean`-полей; в результат подмешиваются `label`, `description`, `required` и `disabled` из `formConfig`.
     * @returns {@link BusinessFormCheckboxProps}
     *
     * @example
     * ```tsx
     * <Checkbox {...form.field("isActive").getCheckboxProps()} />
     * ```
     */
    getCheckboxProps: TName extends BooleanFieldPath<TFieldValues>
        ? () => BusinessFormCheckboxProps<TFieldValues, TName>
        : never;
};

/** Render-props API `BusinessForm`. */
export type BusinessFormRenderProps<TFieldValues extends FieldValues> = Omit<
    FormRenderProps<TFieldValues>,
    "field" | "setValue"
> & {
    setValue: <TName extends FieldPath<TFieldValues>>(
        name: TName,
        value: FieldPathValue<TFieldValues, TName>,
        options?: BusinessFormSetValueOptions,
    ) => void;
    field: <TName extends FieldPath<TFieldValues>>(name: TName) => BusinessFormFieldBinding<TFieldValues, TName>;
};

/** Пропсы `BusinessForm`. */
export type BusinessFormProps<TFieldValues extends FieldValues = FieldValues> = Omit<
    FormProps<TFieldValues>,
    "children" | "onError" | "schemaConfig" | "initialValues" | "onSubmit" | "resolver"
> & {
    children: React.ReactNode | ((form: BusinessFormRenderProps<TFieldValues>) => React.ReactNode);
    /**
     * Авторитетный initial snapshot формы. \
     * При наличии полностью отключает `seedValues`, submission memory и defaults из `formConfig`.
     */
    initialValues?: TFieldValues;
    /**
     * Контекстные значения автозаполнения для формы без `initialValues`. \
     * Применяются только к полям без remembered value; `undefined` разрешает fallback, остальные значения, включая `null`, считаются заданными.
     */
    seedValues?: Partial<TFieldValues> | null;
    formConfig: FormConfigForBusinessForm<TFieldValues & object, void>;
    formTitle?: string;
    ready?: boolean;
    fallback?: React.ReactNode;
    disabled?: boolean;
    disabledValidationPolicy?: "skip" | "validate";
    onError?: false | BusinessFormOnError<TFieldValues>;
    showDefaultValidationErrorNotification?: boolean;
    onSubmit?: FormProps<TFieldValues>["onSubmit"];
    /**
     * Best-effort persistence последних успешно сабмитнутых значений. \
     * Save выполняется после resolved submit callback; restore — до mount RHF.
     */
    submissionMemory?: FormSubmissionMemoryOptions<TFieldValues>;
};

export type { FormSubmissionMemoryOptions };

/** Имена полей с `number | null` в форме. */
type NullableNumberFieldPath<TFieldValues extends FieldValues> = {
    [K in FieldPath<TFieldValues>]: null extends FieldPathValue<TFieldValues, K> ? K : never;
}[FieldPath<TFieldValues>];

/** Имена strict `number`-полей в форме. */
type StrictNumberFieldPath<TFieldValues extends FieldValues> = {
    [K in FieldPath<TFieldValues>]: NonNullable<FieldPathValue<TFieldValues, K>> extends number ? K : never;
}[FieldPath<TFieldValues>];

/** Имена числовых полей для {@link BusinessFormFieldBinding.getNumberInputProps}. */
type NumberFieldPath<TFieldValues extends FieldValues> =
    | NullableNumberFieldPath<TFieldValues>
    | StrictNumberFieldPath<TFieldValues>;

/** Имена boolean-полей для {@link BusinessFormFieldBinding.getCheckboxProps}. */
type BooleanFieldPath<TFieldValues extends FieldValues> = {
    [K in FieldPath<TFieldValues>]: FieldPathValue<TFieldValues, K> extends boolean ? K : never;
}[FieldPath<TFieldValues>];

/**
 * Пропсы числового input: `value` — string для DOM, в форме — `number | null`.
 * @template TFieldValues тип значений формы
 * @template TName имя поля
 */
export type BusinessFormNumberInputProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
    FormFieldInputProps<TFieldValues, TName>,
    "value"
> & {
    value: string;
} & Pick<BusinessFormFieldMeta, "label" | "required" | "disabled">;

/**
 * Пропсы checkbox-контрола для boolean-поля `BusinessForm`.
 * @template TFieldValues тип значений формы
 * @template TName имя boolean-поля
 */
export type BusinessFormCheckboxProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Pick<
    CheckboxProps,
    "checked" | "onChange" | "onBlur" | "error"
> &
    Pick<BusinessFormFieldMeta, "label" | "description" | "required" | "disabled"> & {
        /** Имя поля формы для native checkbox. */
        name: TName;
    };

/** Опции {@link BusinessFormFieldBinding.getNumberInputProps} для nullable number. */
export type BusinessFormNullableNumberInputOptions = {
    /** Пустое/невалидное значение сохраняется как `null`. */
    nullable: true;
};

/** Опции {@link BusinessFormFieldBinding.getNumberInputProps} для strict number. */
export type BusinessFormStrictNumberInputOptions = {
    /** Пустое/невалидное значение сохраняется как `0`. */
    nullable?: false;
};

function assignFormHandleRef<TFieldValues extends FieldValues>(
    ref: React.Ref<FormHandle<TFieldValues>> | undefined,
    value: FormHandle<TFieldValues> | null,
): void {
    if (typeof ref === "function") {
        ref(value);
        return;
    }
    if (ref != null && typeof ref === "object" && "current" in ref) {
        (ref as React.MutableRefObject<FormHandle<TFieldValues> | null>).current = value;
    }
}

function getValueFingerprint(value: unknown): string {
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function shouldValidateAfterSetValue(rhfOptions: SetValueConfig | undefined, validateOnSetValueDefault: boolean): boolean {
    if (rhfOptions?.shouldValidate === false) return false;
    if (rhfOptions?.shouldValidate === true) return true;
    return validateOnSetValueDefault;
}

function writeBooleanFieldValue<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    setValue: BusinessFormRenderProps<TFieldValues>["setValue"],
    name: TName,
    value: boolean,
): void {
    // TODO(types): remove assertion after RHF FieldPathValue narrows boolean-backed paths through BooleanFieldPath.
    setValue(name, value as FieldPathValue<TFieldValues, TName>);
}

function writeFieldValue<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    setValue: BusinessFormRenderProps<TFieldValues>["setValue"],
    name: TName,
    value: FieldPathValue<TFieldValues, TName> | null,
): void {
    // TODO(types): RHF PathValueImpl не всегда включает null в union для nullable полей формы.
    setValue(name, value as FieldPathValue<TFieldValues, TName>);
}

function getNullableNonStringSelectProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    form: FormRenderProps<TFieldValues>,
    name: TName,
    setValue: BusinessFormRenderProps<TFieldValues>["setValue"],
): FormFieldControlledPropsWithLabel<TFieldValues, TName> {
    const base = form.field(name).getControlledPropsWithLabel();
    const rawValue = base.value;
    return {
        ...base,
        // TODO(types): UI SearchSelect использует null для пустого значения nullable-backed полей.
        value: (rawValue ?? null) as FormFieldControlledPropsWithLabel<TFieldValues, TName>["value"],
        onChange: (next) => {
            if (next === null) {
                writeFieldValue(setValue, name, null);
                return;
            }
            setValue(name, next);
        },
    };
}

function getSelectProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    form: FormRenderProps<TFieldValues>,
    name: TName,
    setValue: BusinessFormRenderProps<TFieldValues>["setValue"],
): FormFieldControlledPropsWithLabel<TFieldValues, TName> {
    const base = form.field(name).getControlledPropsWithLabel();
    const rawValue = base.value;
    return {
        ...base,
        value: (rawValue === "" ? null : rawValue) as FormFieldControlledPropsWithLabel<TFieldValues, TName>["value"],
        onChange: (next) => {
            if (next === null) {
                // TODO(types): очистка string-backed select пишет "" в форму.
                setValue(name, "" as FieldPathValue<TFieldValues, TName>);
                return;
            }
            setValue(name, next);
        },
    };
}

/**
 * Адаптер для числовых полей: DOM string ↔ `number | null` в RHF.
 * @param form render-API формы
 * @param name имя числового поля
 * @param setValue guarded setValue из BusinessForm
 * @param options nullable или strict number
 */
function getNumberInputProps<TFieldValues extends FieldValues, TName extends NumberFieldPath<TFieldValues>>(
    form: FormRenderProps<TFieldValues>,
    name: TName,
    setValue: BusinessFormRenderProps<TFieldValues>["setValue"],
    options?: BusinessFormNullableNumberInputOptions | BusinessFormStrictNumberInputOptions,
): BusinessFormNumberInputProps<TFieldValues, TName> {
    const base = form.field(name).getInputProps();
    const raw = form.watch(name);
    const value = typeof raw === "number" && !Number.isNaN(raw) ? String(raw) : "";
    const nullable = options?.nullable === true;

    return {
        ...base,
        value,
        onChange: (e: FormFieldNativeStringChangeEvent) => {
            const next = e.target.value;
            if (next === "" || next === "NaN") {
                setValue(name, (nullable ? null : 0) as FieldPathValue<TFieldValues, TName>);
                return;
            }
            const parsed = Number(next);
            setValue(name, (Number.isNaN(parsed) ? (nullable ? null : 0) : parsed) as FieldPathValue<TFieldValues, TName>);
        },
    };
}

/**
 * Адаптер для checkbox-полей: native `checked` ↔ boolean в RHF.
 * @param form render-API формы
 * @param name имя boolean-поля
 * @param setValue guarded setValue из BusinessForm
 */
function getCheckboxProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    form: FormRenderProps<TFieldValues>,
    name: TName,
    setValue: BusinessFormRenderProps<TFieldValues>["setValue"],
): Omit<BusinessFormCheckboxProps<TFieldValues, TName>, "label" | "description" | "required" | "disabled"> {
    const err = form.getFieldState(name).error;

    return {
        name,
        checked: form.watch(name) === true,
        onChange: (event) => {
            writeBooleanFieldValue(setValue, name, event.target.checked);
        },
        onBlur: () => void form.trigger(name),
        error: err != null,
    };
}

function toBusinessFormRenderProps<TFieldValues extends FieldValues>(
    form: FormRenderProps<TFieldValues>,
    options?: {
        resolveFieldMeta?: (name: string, values: TFieldValues) => BusinessFormFieldMeta;
        effectiveSchemaConfig?: FormSchemaConfig<Record<string, unknown>>;
        globalDisabled?: boolean;
        setValueValidationDeps?: Readonly<Partial<Record<string, readonly string[]>>>;
        validateOnSetValueDefault?: boolean;
    },
): BusinessFormRenderProps<TFieldValues> {
    const getFieldMeta = (name: FieldPath<TFieldValues>, values: TFieldValues): BusinessFormFieldMeta => {
        if (options?.resolveFieldMeta == null) {
            return { label: form.field(name).getLabel() };
        }
        return options.resolveFieldMeta(String(name), values);
    };

    const isFieldDisabled = (name: FieldPath<TFieldValues>, values: TFieldValues): boolean =>
        options?.globalDisabled === true || getFieldMeta(name, values).disabled === true;

    const validateOnSetValueDefault = options?.validateOnSetValueDefault ?? true;
    const setValueValidationDeps = options?.setValueValidationDeps;

    const guardedSetValue: BusinessFormRenderProps<TFieldValues>["setValue"] = (name, value, setValueOptions) => {
        const values = form.getValues();
        const allowDisabledWrite = setValueOptions?.allowDisabledWrite === true;
        if (isFieldDisabled(name, values) && !allowDisabledWrite) {
            if (process.env.NODE_ENV !== "production") {
                console.warn(`[BusinessForm] setValue("${String(name)}") blocked: field is disabled.`);
            }
            return;
        }
        const { allowDisabledWrite: _omitAllowDisabledWrite, reason: _omitReason, ...rhfOptions } = setValueOptions ?? {};
        void _omitAllowDisabledWrite;
        void _omitReason;
        form.setValue(name, value, rhfOptions);

        if (setValueValidationDeps != null && shouldValidateAfterSetValue(rhfOptions, validateOnSetValueDefault)) {
            const deps = setValueValidationDeps[String(name)];
            if (deps != null && deps.length > 0) {
                void form.trigger(deps as FieldPath<TFieldValues>[]);
            }
        }
    };

    return {
        ...form,
        setValue: guardedSetValue,
        field: (<TName extends FieldPath<TFieldValues>>(name: TName) => {
            const baseField = form.field(name);
            const getMeta = (): BusinessFormFieldMeta => getFieldMeta(name, form.getValues());
            const getUiProps = (): Pick<BusinessFormFieldMeta, "label" | "description" | "required" | "disabled"> => {
                const meta = getMeta();
                return {
                    label: meta.label,
                    description: meta.description,
                    required: meta.required,
                    disabled: options?.globalDisabled === true || meta.disabled === true,
                };
            };

            return {
                name,
                ...baseField,
                getInputProps: () => {
                    const base = baseField.getInputProps();
                    const meta = getUiProps();
                    return {
                        ...base,
                        onChange: (e: FormFieldNativeStringChangeEvent) =>
                            guardedSetValue(name, e.target.value as FieldPathValue<TFieldValues, TName>),
                        label: meta.label,
                        required: meta.required,
                        disabled: meta.disabled,
                    };
                },
                getControlledProps: () => {
                    const base = baseField.getControlledProps();
                    const meta = getUiProps();
                    return {
                        ...base,
                        onChange: (next: FieldPathValue<TFieldValues, TName>) => guardedSetValue(name, next),
                        label: meta.label,
                        required: meta.required,
                        disabled: meta.disabled,
                    };
                },
                getControlledPropsWithLabel: () => {
                    const base = baseField.getControlledProps();
                    const meta = getUiProps();
                    return {
                        ...base,
                        onChange: (next: FieldPathValue<TFieldValues, TName>) => guardedSetValue(name, next),
                        label: meta.label,
                        required: meta.required,
                        disabled: meta.disabled,
                    };
                },
                getPropsWithLabel: () => {
                    const base = baseField.getInputProps();
                    const meta = getUiProps();
                    return {
                        ...base,
                        onChange: (e: FormFieldNativeStringChangeEvent) =>
                            guardedSetValue(name, e.target.value as FieldPathValue<TFieldValues, TName>),
                        label: meta.label,
                        required: meta.required,
                        disabled: meta.disabled,
                    };
                },
                getUiProps,
                getMeta,
                getSearchSelectProps: <TEntity extends object>(
                    mapEntityToValue: (entity: TEntity | null) => FieldPathValue<TFieldValues, TName> | null,
                ) => {
                    const raw = form.getValues(name);
                    const useStringEmptySentinel = raw === undefined || raw === "" || typeof raw === "string";
                    const base = useStringEmptySentinel
                        ? getSelectProps(form, name, guardedSetValue)
                        : getNullableNonStringSelectProps(form, name, guardedSetValue);
                    const meta = getUiProps();
                    return {
                        ...base,
                        label: meta.label,
                        required: meta.required,
                        disabled: meta.disabled,
                        onChange: (entity: TEntity | null) => {
                            const mapped = mapEntityToValue(entity);
                            if (mapped === null) {
                                writeFieldValue(guardedSetValue, name, null);
                                return;
                            }
                            guardedSetValue(name, mapped);
                        },
                    };
                },
                getDateInputProps: () => {
                    const base = baseField.getControlledPropsWithLabel();
                    const meta = getUiProps();
                    return {
                        ...base,
                        label: meta.label,
                        required: meta.required,
                        disabled: meta.disabled,
                        onChange: (next: FieldPathValue<TFieldValues, TName>) => guardedSetValue(name, next),
                    };
                },
                getNumberInputProps: (
                    numberOptions?: BusinessFormNullableNumberInputOptions | BusinessFormStrictNumberInputOptions,
                ) => {
                    const meta = getUiProps();
                    const num = getNumberInputProps(
                        form,
                        name as unknown as NumberFieldPath<TFieldValues>,
                        guardedSetValue,
                        numberOptions,
                    ) as BusinessFormNumberInputProps<TFieldValues, TName>;
                    return { ...num, label: meta.label, required: meta.required, disabled: meta.disabled };
                },
                getCheckboxProps: () => {
                    const meta = getUiProps();
                    const checkbox = getCheckboxProps(form, name, guardedSetValue);
                    return {
                        ...checkbox,
                        label: meta.label,
                        description: meta.description,
                        required: meta.required,
                        disabled: meta.disabled,
                    };
                },
            };
        }) as unknown as BusinessFormRenderProps<TFieldValues>["field"],
    };
}

const BusinessFormRoot = React.forwardRef(function BusinessFormInner<TFieldValues extends FieldValues = FieldValues>(
    props: BusinessFormProps<TFieldValues>,
    ref: React.Ref<FormHandle<TFieldValues>>,
) {
    const {
        children,
        ready = true,
        fallback = null,
        formConfig,
        disabled = false,
        initialValues,
        seedValues,
        testId,
        submitOnCtrlEnter = true,
        focusOnContainerClick = true,
        validateOnSetValue = true,
        onError: businessFormOnError,
        showDefaultValidationErrorNotification: showDefaultValidationErrorNotificationProp,
        onSubmit,
        submissionMemory,
        onSubmitExecutionError: userOnSubmitExecutionError,
        ...restFormProps
    } = props;

    const { rememberedValues, wrapSubmitHandler } = useFormSubmissionMemory(submissionMemory, initialValues === undefined);
    const defaultValues = useMemo(() => formConfig.defaults(), [formConfig]);
    const effectiveInitialValues = useMemo(
        () =>
            resolveBusinessFormInitialValues({
                initialValues,
                seedValues,
                rememberedValues,
                defaultValues,
            }),
        [defaultValues, initialValues, rememberedValues, seedValues],
    );

    const wrappedOnSubmit = useMemo((): FormProps<TFieldValues>["onSubmit"] => {
        const memoryWrapped = wrapSubmitHandler(onSubmit);
        if (memoryWrapped == null) {
            return onSubmit;
        }

        return async (data) => {
            await memoryWrapped(data);
        };
    }, [onSubmit, wrapSubmitHandler]);

    const effectiveSchemaConfig = formConfig.schemaConfig;
    const effectiveResolver = formConfig.resolver;
    const latestFormValuesRef = useRef<TFieldValues | null>(null);
    const showDefaultValidationToast =
        businessFormOnError === false
            ? false
            : businessFormOnError === undefined
              ? true
              : (showDefaultValidationErrorNotificationProp ?? false);

    const handleBusinessFormValidationErrors = useCallback(
        (errors: FieldErrors<TFieldValues>) => {
            const ctx: BusinessFormSubmitErrorContext<TFieldValues> = toBusinessFormSubmitErrorContext(errors);
            if (showDefaultValidationToast) {
                const values = latestFormValuesRef.current ?? ({} as TFieldValues);
                const captionOptions: BusinessFormValidationCaptionOptions = {
                    resolveFieldLabel: (segment) => {
                        const meta = formConfig.resolveFieldMeta?.(segment, values);
                        const label = meta?.label?.trim();
                        return label === "" ? undefined : label;
                    },
                };
                showDefaultBusinessFormValidationWarning(ctx, "Проверьте форму", effectiveSchemaConfig, captionOptions);
            }
            if (typeof businessFormOnError === "function") {
                businessFormOnError(ctx);
            }
        },
        [showDefaultValidationToast, businessFormOnError, effectiveSchemaConfig, formConfig],
    );

    const setFormRef = useCallback(
        (api: FormHandle<TFieldValues> | null) => {
            if (api == null) {
                assignFormHandleRef(ref, null);
                return;
            }

            const wrappedHandleSubmit: FormHandle<TFieldValues>["handleSubmit"] = (onValid, onInvalid) => {
                const submitHandler = wrapSubmitHandler(onValid) ?? onValid;
                return api.handleSubmit(submitHandler, onInvalid);
            };

            assignFormHandleRef(ref, {
                ...api,
                handleSubmit: wrappedHandleSubmit,
            });
        },
        [ref, wrapSubmitHandler],
    );

    const reportSubmitExecutionError = useCallback(
        (error: unknown) => {
            userOnSubmitExecutionError?.(error);
            reportError(error, { from: "BusinessForm.submit" });
        },
        [userOnSubmitExecutionError],
    );

    const initialValuesFingerprintRef = useRef<string>(getValueFingerprint(effectiveInitialValues));
    const formBodyMountedRef = useRef(false);

    useEffect(() => {
        if (process.env.NODE_ENV === "production") return;
        if (!ready) {
            formBodyMountedRef.current = false;
            return;
        }
        const nextFingerprint = getValueFingerprint(effectiveInitialValues);
        if (!formBodyMountedRef.current) {
            formBodyMountedRef.current = true;
            initialValuesFingerprintRef.current = nextFingerprint;
            return;
        }
        if (initialValuesFingerprintRef.current !== nextFingerprint) {
            console.warn(
                "[BusinessForm] resolved initial values changed after mount; RHF will not auto-sync. Use reset() or remount.",
            );
            initialValuesFingerprintRef.current = nextFingerprint;
        }
    }, [effectiveInitialValues, ready]);

    const setValueValidationDeps = useMemo(
        () => buildSetValueValidationDepsMap(formConfig.fieldDeclarations),
        [formConfig.fieldDeclarations],
    );

    return (
        <RenderWhen when={ready} fallback={fallback}>
            <FormPrimitive
                ref={setFormRef}
                {...restFormProps}
                resolver={effectiveResolver as FormProps<TFieldValues>["resolver"]}
                schemaConfig={effectiveSchemaConfig}
                initialValues={effectiveInitialValues}
                testId={testId}
                submitOnCtrlEnter={submitOnCtrlEnter}
                focusOnContainerClick={focusOnContainerClick}
                validateOnSetValue={validateOnSetValue}
                onError={businessFormOnError === false ? undefined : handleBusinessFormValidationErrors}
                onSubmit={wrappedOnSubmit}
                onSubmitExecutionError={reportSubmitExecutionError}
            >
                {(form) => {
                    latestFormValuesRef.current = form.getValues();
                    return typeof children === "function"
                        ? children(
                              toBusinessFormRenderProps(form, {
                                  resolveFieldMeta: formConfig.resolveFieldMeta,
                                  effectiveSchemaConfig,
                                  globalDisabled: disabled,
                                  setValueValidationDeps,
                                  validateOnSetValueDefault: validateOnSetValue,
                              }),
                          )
                        : children;
                }}
            </FormPrimitive>
        </RenderWhen>
    );
}) as <TFieldValues extends FieldValues = FieldValues>(
    props: BusinessFormProps<TFieldValues> & { ref?: React.Ref<FormHandle<TFieldValues>> },
) => React.ReactElement | null;

/** Декларативная business-форма поверх RHF `Form`. */
export const BusinessForm = Object.assign(BusinessFormRoot, {
    Section: FormPrimitive.Section,
    Row: FormPrimitive.Row,
    Field: FormPrimitive.Field,
});

export type { FormHandle } from "@src/components/form";
export type { BusinessFormOnError, BusinessFormSubmitErrorContext } from "./businessFormValidationError";
